import { useState } from 'react';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';
import { ESQUEMA_CRM } from '@/lib/filtros-esquemas';

/** Los filtros que esta pantalla guarda en la URL. */
interface FiltrosCrm {
  search?: string;
  tagId?: string;
  modelo?: string;
  vista?: string;
  sort?: string;
}
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Phone,
  Tag as TagIcon,
  GripVertical,
  KanbanSquare,
  Table as TableIcon,
  ArrowUp,
  ArrowDown,
  Download,
} from 'lucide-react';
import { hasRole, type LeadStatus } from '@conversia/shared-types';
import { useAuthStore } from '@/store/auth.store';
import type { Paginated, ConversationWithRelations } from '@/lib/types';
import * as conversationsApi from '@/api/conversations.api';
import * as tagsApi from '@/api/tags.api';
import { PageHeader } from '@/components/common/PageHeader';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { relativeTime, formatPhoneNumber } from '@/lib/format';
import { cn } from '@/lib/cn';
import { FiltrosGuardados } from '@/components/filtros/FiltrosGuardados';
import { CajaDeBusqueda } from '@/components/common/CajaDeBusqueda';
import {
  PIPELINE_COLUMNS,
  buildPipeline,
  buildTableRows,
  withLeadStatus,
  type Lead,
  type PipelineColumn,
  type TableSort,
  type TableSortKey,
} from '@/lib/crm';

/** Cache key del tablero, parametrizada por el buscador (server-side). */
const leadsKey = (search: string) => ['crm', 'leads', search] as const;
/** Tope de leads cargados (el endpoint pagina; suficiente para el pipeline). */
const LIST_TAKE = 200;
/** MIME del payload de drag (qué lead y desde qué columna). */
const DRAG_MIME = 'application/x-conversia-lead';

/** La vista elegida (kanban/tabla) se recuerda por navegador (F5). */
const VISTA_KEY = 'conversia.crm.vista';

type Vista = 'kanban' | 'tabla';

function readVista(): Vista {
  try {
    return localStorage.getItem(VISTA_KEY) === 'tabla' ? 'tabla' : 'kanban';
  } catch {
    return 'kanban';
  }
}

/**
 * Vista CRM / Pipeline: un kanban con TODOS los leads clasificados, una columna
 * por temperatura (LeadStatus). Reusa `GET /api/conversations` (con contacto,
 * tags y profileDataJson) para los datos y `PATCH /api/conversations/:id/lead-status`
 * para mover un lead entre columnas (update optimista + rollback). Sólo lectura
 * salvo ese PATCH ya existente; no toca el flujo de la bandeja.
 */
export default function Crm() {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role ? hasRole(role, 'ADMIN') : false;

  // A1: los filtros viven en la URL. `overStatus` NO entra: es estado de hover.
  const { filtros, setFiltros } = useFiltrosUrl<FiltrosCrm>(ESQUEMA_CRM);

  const search = filtros.search ?? '';
  const setSearch = (v: string) => setFiltros({ search: v || undefined });

  const tagId = filtros.tagId ?? '';
  const setTagId = (v: string) => setFiltros({ tagId: v || undefined });

  const modelo = filtros.modelo ?? '';
  const setModelo = (v: string) => setFiltros({ modelo: v || undefined });
  const [overStatus, setOverStatus] = useState<LeadStatus | null>(null);
  // La vista es una PREFERENCIA, no un filtro: sigue viviendo en localStorage y
  // ese es el default. La URL solo la pisa cuando viene explicita, para que un
  // enlace pueda decir "mira esto en tabla" sin borrarle la preferencia a nadie.
  const vista: Vista =
    filtros.vista === 'tabla' || filtros.vista === 'kanban'
      ? (filtros.vista as Vista)
      : readVista();
  const setVista = (v: Vista) => setFiltros({ vista: v });
  const [sort, setSort] = useState<TableSort | undefined>(undefined);

  function cambiarVista(v: Vista) {
    setVista(v);
    try {
      localStorage.setItem(VISTA_KEY, v);
    } catch {
      /* sin localStorage: la preferencia vive solo esta sesión */
    }
  }

  function toggleSort(key: TableSortKey) {
    setSort((s) =>
      s?.key === key ? (s.dir === 'asc' ? { key, dir: 'desc' } : undefined) : { key, dir: 'asc' },
    );
  }

  const { data, isLoading } = useQuery({
    queryKey: leadsKey(search),
    queryFn: () =>
      conversationsApi.listConversations({ search: search || undefined, take: LIST_TAKE }),
  });

  const tagsQuery = useQuery({
    queryKey: ['tags', 'CONTACT'],
    queryFn: () => tagsApi.listTags('CONTACT'),
  });

  const filters = { tagId: tagId || undefined, modelo: modelo || undefined };
  const groups = buildPipeline(data?.items ?? [], filters);
  const rows = vista === 'tabla' ? buildTableRows(data?.items ?? [], filters, sort) : [];

  const move = useMutation({
    mutationFn: ({ conversationId, to }: { conversationId: string; to: LeadStatus }) =>
      conversationsApi.updateLeadStatus(conversationId, to),
    // Update OPTIMISTA: movemos la card en la cache antes de la respuesta.
    onMutate: async ({ conversationId, to }) => {
      const key = leadsKey(search);
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<Paginated<ConversationWithRelations>>(key);
      if (prev) {
        qc.setQueryData<Paginated<ConversationWithRelations>>(key, {
          ...prev,
          items: withLeadStatus(prev.items, conversationId, to),
        });
      }
      return { prev, key };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(ctx.key, ctx.prev); // rollback
      toast.error('No se pudo mover el lead');
    },
    onSuccess: () => toast.success('Lead actualizado'),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['crm', 'leads'] });
      // La bandeja comparte el dato: que refleje el nuevo estado.
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });

  const handleDrop = (to: LeadStatus) => (e: React.DragEvent) => {
    e.preventDefault();
    setOverStatus(null);
    const raw = e.dataTransfer.getData(DRAG_MIME);
    if (!raw) return;
    try {
      const { conversationId, from } = JSON.parse(raw) as {
        conversationId: string;
        from: LeadStatus;
      };
      if (from !== to) move.mutate({ conversationId, to });
    } catch {
      /* payload inválido: se ignora */
    }
  };

  const total = PIPELINE_COLUMNS.reduce((n, col) => n + groups[col.status].length, 0);

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <PageHeader
        title="CRM · Pipeline"
        helpKey="crm"
        description="Todos los leads clasificados por temperatura. Arrastrá una tarjeta para cambiar su estado."
      />
      {/* A2 · Filtros guardados. Lee la ruta y el ?query de la URL por su cuenta. */}
      <FiltrosGuardados />

      {/* Buscador + filtros (reusan el listado de la bandeja y el sistema de tags) */}
      <div className="flex flex-wrap items-center gap-2">
        {/* A7 · La misma caja que en Clientes, Contactos y la bandeja. */}
        <CajaDeBusqueda valor={search} onChange={setSearch} className="min-w-56 flex-1" />
        <input
          value={modelo}
          onChange={(e) => setModelo(e.target.value)}
          placeholder="Modelo de interés…"
          className="h-9 w-48 rounded-md border border-surface-border bg-white px-3 text-sm focus:border-brand-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
        />
        <select
          value={tagId}
          onChange={(e) => setTagId(e.target.value)}
          className="h-9 w-44 rounded-md border border-surface-border bg-white px-2 text-sm focus:border-brand-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
        >
          <option value="">Todas las etiquetas</option>
          {(tagsQuery.data ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>

        {/* Export XLSX (F3, ADMIN): tope duro de 50.000 filas del lado servidor. */}
        {isAdmin && (
          <button
            type="button"
            onClick={() => {
              void conversationsApi
                .downloadCrmExport()
                .then(() => toast.success('Excel generado'))
                .catch(() => toast.error('No se pudo generar el Excel'));
            }}
            className="ml-auto flex items-center gap-1.5 rounded-md border border-surface-border bg-white px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <Download className="h-4 w-4" /> Excel
          </button>
        )}

        {/* Toggle de vista (F5): tablero o tabla, recordado por navegador. */}
        <div
          className={cn(
            'flex overflow-hidden rounded-md border border-surface-border dark:border-slate-700',
            !isAdmin && 'ml-auto',
          )}
        >
          {(
            [
              { v: 'kanban' as Vista, icon: KanbanSquare, label: 'Tablero' },
              { v: 'tabla' as Vista, icon: TableIcon, label: 'Tabla' },
            ] as const
          ).map(({ v, icon: Icon, label }) => (
            <button
              key={v}
              type="button"
              onClick={() => cambiarVista(v)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors',
                vista === v
                  ? 'bg-brand-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800',
              )}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>
      </div>

      {/* Tablero o tabla */}
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : vista === 'tabla' ? (
        <LeadTable
          rows={rows}
          sort={sort}
          onToggleSort={toggleSort}
          onOpen={(conversationId) => navigate(`/conversations/${conversationId}`)}
          onChangeStatus={(conversationId, from, to) => {
            if (from !== to) move.mutate({ conversationId, to });
          }}
        />
      ) : (
        <div className="flex flex-1 gap-3 overflow-x-auto pb-2">
          {PIPELINE_COLUMNS.map((col) => (
            <PipelineColumnView
              key={col.status}
              column={col}
              leads={groups[col.status]}
              isOver={overStatus === col.status}
              onDragOverColumn={() => setOverStatus(col.status)}
              onDragLeaveColumn={() => setOverStatus((s) => (s === col.status ? null : s))}
              onDrop={handleDrop(col.status)}
              onOpen={(conversationId) => navigate(`/conversations/${conversationId}`)}
            />
          ))}
        </div>
      )}

      {!isLoading && total === 0 && (
        /* A7 · Este vacío ya decía de dónde sale el dato; se le agrega el paso
           concreto para que no haya que deducirlo. */
        <p className="text-center text-sm text-slate-400">
          No hay leads clasificados todavía.
          <br />
          <span className="text-xs">
            La temperatura se asigna desde la ficha del lead, o la pone la asesora sola cuando
            atiende. Abre una conversación y clasifica el lead para verlo acá.
          </span>
        </p>
      )}
    </div>
  );
}

/** Vista de tabla (F5): mismos leads del tablero, planos y ordenables. */
function LeadTable({
  rows,
  sort,
  onToggleSort,
  onOpen,
  onChangeStatus,
}: {
  rows: Lead[];
  sort: TableSort | undefined;
  onToggleSort: (key: TableSortKey) => void;
  onOpen: (conversationId: string) => void;
  onChangeStatus: (conversationId: string, from: LeadStatus, to: LeadStatus) => void;
}) {
  const headers: { key: TableSortKey | null; label: string }[] = [
    { key: 'name', label: 'Lead' },
    { key: 'leadStatus', label: 'Temperatura' },
    { key: 'modelo', label: 'Modelo' },
    { key: null, label: 'Presupuesto' },
    { key: null, label: 'Etiquetas' },
    { key: 'lastContactAt', label: 'Último contacto' },
  ];

  return (
    <div className="flex-1 overflow-auto rounded-xl border border-surface-border dark:border-slate-800">
      <table className="w-full min-w-[720px] text-sm">
        <thead className="sticky top-0 bg-slate-50 text-left dark:bg-slate-900">
          <tr>
            {headers.map((h) => (
              <th
                key={h.label}
                className="whitespace-nowrap border-b border-surface-border px-3 py-2 font-semibold text-slate-600 dark:border-slate-800 dark:text-slate-300"
              >
                {h.key ? (
                  <button
                    type="button"
                    onClick={() => onToggleSort(h.key!)}
                    className="flex items-center gap-1 hover:text-slate-900 dark:hover:text-slate-100"
                  >
                    {h.label}
                    {sort?.key === h.key &&
                      (sort.dir === 'asc' ? (
                        <ArrowUp className="h-3.5 w-3.5" />
                      ) : (
                        <ArrowDown className="h-3.5 w-3.5" />
                      ))}
                  </button>
                ) : (
                  h.label
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-surface-border dark:divide-slate-800">
          {rows.length === 0 ? (
            <tr>
              {/* A7 · El vacío dice qué hacer. */}
              <td colSpan={headers.length} className="px-3 py-8 text-center text-slate-400">
                No hay leads con estos filtros.
                <br />
                <span className="text-xs">
                  Prueba quitando el filtro de temperatura o borrando lo que escribiste en el
                  buscador.
                </span>
              </td>
            </tr>
          ) : (
            rows.map((lead) => (
              <tr
                key={lead.conversationId}
                onClick={() => onOpen(lead.conversationId)}
                className="cursor-pointer bg-white transition-colors hover:bg-slate-50 dark:bg-slate-900/60 dark:hover:bg-slate-800/60"
              >
                <td className="max-w-56 px-3 py-2.5">
                  <p className="truncate font-medium text-slate-900 dark:text-slate-100">
                    {lead.name}
                  </p>
                  {lead.phone && (
                    <p className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                      <Phone className="h-3 w-3" /> {formatPhoneNumber(lead.phone)}
                    </p>
                  )}
                </td>
                <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                  <select
                    value={lead.leadStatus}
                    onChange={(e) =>
                      onChangeStatus(
                        lead.conversationId,
                        lead.leadStatus,
                        e.target.value as LeadStatus,
                      )
                    }
                    className="h-8 rounded-md border border-surface-border bg-white px-2 text-xs focus:border-brand-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                  >
                    {PIPELINE_COLUMNS.map((c) => (
                      <option key={c.status} value={c.status}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="max-w-40 truncate px-3 py-2.5 text-slate-700 dark:text-slate-200">
                  {lead.modelo ?? '—'}
                </td>
                <td className="max-w-32 truncate px-3 py-2.5 text-slate-700 dark:text-slate-200">
                  {lead.presupuesto ?? '—'}
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex max-w-44 flex-wrap gap-1">
                    {lead.tags.map((t) => (
                      <span
                        key={t.id}
                        className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium text-white"
                        style={{ backgroundColor: t.color }}
                      >
                        <TagIcon className="h-2.5 w-2.5" /> {t.name}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 text-xs text-slate-400">
                  {lead.lastContactAt ? relativeTime(lead.lastContactAt) : '—'}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function PipelineColumnView({
  column,
  leads,
  isOver,
  onDragOverColumn,
  onDragLeaveColumn,
  onDrop,
  onOpen,
}: {
  column: PipelineColumn;
  leads: Lead[];
  isOver: boolean;
  onDragOverColumn: () => void;
  onDragLeaveColumn: () => void;
  onDrop: (e: React.DragEvent) => void;
  onOpen: (conversationId: string) => void;
}) {
  return (
    <section
      onDragOver={(e) => {
        e.preventDefault();
        onDragOverColumn();
      }}
      onDragLeave={onDragLeaveColumn}
      onDrop={onDrop}
      className={cn(
        'flex w-72 shrink-0 flex-col rounded-xl border bg-slate-50/60 dark:bg-slate-900/40',
        isOver
          ? 'border-brand-400 ring-2 ring-brand-400/30'
          : 'border-surface-border dark:border-slate-800',
      )}
    >
      <header className="flex items-center gap-2 px-3 py-2.5">
        <span className={cn('h-2.5 w-2.5 rounded-full', column.dot)} />
        <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">{column.label}</h2>
        <span className="ml-auto rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
          {leads.length}
        </span>
      </header>
      <div className="flex-1 space-y-2 overflow-y-auto px-2 pb-2">
        {leads.length === 0 ? (
          <p className="px-1 py-6 text-center text-xs text-slate-400">— Vacío</p>
        ) : (
          leads.map((lead) => (
            <LeadCard
              key={lead.conversationId}
              lead={lead}
              fromStatus={column.status}
              onOpen={onOpen}
            />
          ))
        )}
      </div>
    </section>
  );
}

function LeadCard({
  lead,
  fromStatus,
  onOpen,
}: {
  lead: Lead;
  fromStatus: LeadStatus;
  onOpen: (conversationId: string) => void;
}) {
  return (
    <article
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData(
          DRAG_MIME,
          JSON.stringify({ conversationId: lead.conversationId, from: fromStatus }),
        );
      }}
      onClick={() => onOpen(lead.conversationId)}
      className="group cursor-pointer rounded-lg border border-surface-border bg-white p-3 shadow-sm transition-shadow hover:shadow-md dark:border-slate-800 dark:bg-slate-900"
    >
      <div className="flex items-start gap-1.5">
        <GripVertical className="mt-0.5 h-4 w-4 shrink-0 cursor-grab text-slate-300 group-hover:text-slate-400" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
            {lead.name}
          </p>
          {lead.phone && (
            <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
              <Phone className="h-3 w-3" /> {formatPhoneNumber(lead.phone)}
            </p>
          )}
        </div>
      </div>

      {(lead.modelo || lead.presupuesto) && (
        <dl className="mt-2 space-y-0.5 text-xs">
          {lead.modelo && (
            <div className="flex gap-1.5">
              <dt className="text-slate-400">Modelo</dt>
              <dd className="min-w-0 flex-1 truncate font-medium text-slate-700 dark:text-slate-200">
                {lead.modelo}
              </dd>
            </div>
          )}
          {lead.presupuesto && (
            <div className="flex gap-1.5">
              <dt className="text-slate-400">Presupuesto</dt>
              <dd className="min-w-0 flex-1 truncate font-medium text-slate-700 dark:text-slate-200">
                {lead.presupuesto}
              </dd>
            </div>
          )}
        </dl>
      )}

      {lead.tags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {lead.tags.map((t) => (
            <span
              key={t.id}
              className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium text-white"
              style={{ backgroundColor: t.color }}
            >
              <TagIcon className="h-2.5 w-2.5" /> {t.name}
            </span>
          ))}
        </div>
      )}

      {lead.lastContactAt && (
        <p className="mt-2 text-[11px] text-slate-400">
          Último contacto: {relativeTime(lead.lastContactAt)}
        </p>
      )}
    </article>
  );
}
